import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ClientTypeCode } from '@shared';
import { Observable, shareReplay, tap } from 'rxjs';
import {
    ApiClientType,
    ApiGetAllResponse,
    ApiSingleResponse,
    DashboardCacheType,
} from '../interfaces';
/**
 * Shared utility service providing common API interactions and caching mechanisms.
 *
 * @description
 * Centralizes re-usable logic across domain-specific services, such as fetching
 * client types and managing shared observable caches with automatic failure recovery.
 */
@Injectable({
    providedIn: 'root',
})
export class SharedService {
    /**
     * Injected http service for api calls
     */
    private readonly _http = inject(HttpClient);

    /**
     * Base endpoint for client type api route
     */
    private readonly _baseClientTypeEndpoint = '/api/client-types';

    /**
     * Base endpoint for the user-client-assignment route
     */
    private readonly _baseUserClientInformation = '/api/user-client-assignments';

    /**
     * [WORK-AROUND ONLY]
     *
     * Retrieves the user-client-assignment based on the provided user id as ownerId
     *
     * @remarks
     * We need to do this first since there's no `getUserRoleByUserId` endpoint. This is the only way we can fetch
     * the user clientType by going to the `user-client-assignment` route first then to the `client-type` route
     */
    public getUserClientInformation(userId: string): Observable<
        ApiSingleResponse<{
            clientTypeId: string;
            ownerId: string;
            roleId: string;
        }>
    > {
        return this._http.get<
            ApiSingleResponse<{
                clientTypeId: string;
                ownerId: string;
                roleId: string;
            }>
        >(`${this._baseUserClientInformation}?filters={"ownerId": {"_eq": "${userId}"}}`);
    }

    /**
     * Retrieves the clientType data for the provided client type.
     * Used to retrieve client-information records for dealers ONLY
     *
     * @param clientType - The client type to retrieve the clientType data for
     *
     * @returns The clientType data which contains the clientTypeId which you can use to filter users based on this id
     *
     * @example
     * ```typescript
     * this.dealerService.getClientType(ClientTypeCode.DEALER).subscribe((res) => {
     *  const clientTypeId = res.data[0].clientTypeId;
     * });
     * ```
     */
    public getClientType(clientType: ClientTypeCode): Observable<ApiGetAllResponse<ApiClientType>> {
        return this._http.get<ApiGetAllResponse<ApiClientType>>(
            `${this._baseClientTypeEndpoint}?filters={"clientTypeCode": {"_eq": "${clientType}"}}`,
        );
    }

    /**
     * Cache for the client types request.
     */
    private _clientTypes$: Observable<ApiGetAllResponse<ApiClientType>> | null = null;

    /**
     * Retrieves all client-types (userRoles)
     *
     * @returns Observable of all client-types
     *
     * @example
     * ```typescript
     * console.log(clientTypes); // [{ clientTypeId: '5sads355', clientTypeCode: 'dealer', clientTypeName: 'Dealer'}]
     * ```
     */
    public getAllClientTypes(): Observable<ApiGetAllResponse<ApiClientType>> {
        if (!this._clientTypes$) {
            this._clientTypes$ = this._http
                .get<ApiGetAllResponse<ApiClientType>>(this._baseClientTypeEndpoint)
                .pipe(shareReplay(1));
        }
        return this._clientTypes$;
    }

    /**
     * Generic helper for implementing service-level caching with shareReplay(1).
     *
     * @description
     * If the cache contains the key, returns the cached observable.
     * Otherwise, executes the factory, caches the result with shareReplay(1),
     * and automatically removes the entry if the request fails to allow retries.
     *
     * @template T - The actual data type of the request, constrained to valid dashboard types.
     * @param cache - The Map to store/retrieve the observable from.
     * @param key - Unique lookup key for the request.
     * @param factory - Function that returns the primary request observable.
     * @returns Observable of the requested data type.
     */
    public getCached<T extends DashboardCacheType>(
        cache: Map<string, Observable<DashboardCacheType>>,
        key: string,
        factory: () => Observable<T>,
    ): Observable<T> {
        const cached = cache.get(key) as Observable<T> | undefined;
        if (cached) return cached;

        const request = factory().pipe(
            shareReplay({ bufferSize: 1, refCount: true }),
            tap({
                error: () => cache.delete(key),
            }),
        );

        cache.set(key, request as Observable<DashboardCacheType>);
        return request;
    }
}
